import { useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { Scale, Filter, RotateCcw, Download } from "lucide-react";
import { fetcher } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import "@/i18n/fase3";
import "@/i18n/fase9";

const PAGE_SIZE = 50;
const ANY = "__any__";

function fmtInt(n) {
  return new Intl.NumberFormat("es-ES").format(Math.round(n || 0));
}
function fmtDec(n, d = 1) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n || 0);
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Pesaxes() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { user } = useAuth();
  const allowed =
    user?.role === "ADMIN" ||
    user?.role === "SUPERVISOR" ||
    user?.role === "MANCOMUNIDADE";

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [plate, setPlate] = useState("");
  const [wasteType, setWasteType] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [page, setPage] = useState(0);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(PAGE_SIZE));
    p.set("skip", String(page * PAGE_SIZE));
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (plate) p.set("plate", plate.trim());
    if (wasteType) p.set("waste_type", wasteType);
    if (municipality) p.set("municipality", municipality);
    // If NO date filter is set, ask backend to widen the window to cover 24 months
    if (!dateFrom && !dateTo) p.set("days", "730");
    return p.toString();
  }, [dateFrom, dateTo, plate, wasteType, municipality, page]);

  const { data, isLoading } = useSWR(`/sigeus/weighins?${query}`, fetcher, {
    revalidateOnFocus: false,
  });

  // Standalone SWR for facets (no filter set → drop-down suggestions)
  const { data: facetsData } = useSWR(
    `/sigeus/weighins?limit=1&days=730`,
    fetcher,
    { revalidateOnFocus: false }
  );
  const plates = facetsData?.facets?.plates || [];
  const wasteTypes = facetsData?.facets?.waste_types || [];
  const munis = facetsData?.facets?.municipalities || [];

  const items = data?.weighins || [];
  const total = data?.total_in_range || 0;
  const kgTotal = data?.kg_total_in_range || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const reset = () => {
    setDateFrom("");
    setDateTo("");
    setPlate("");
    setWasteType("");
    setMunicipality("");
    setPage(0);
  };

  const exportCsv = () => {
    if (items.length === 0) return;
    const headers = [
      "weighing_date",
      "vehicle_plate",
      "waste_type",
      "municipality",
      "col_point_id",
      "container_code",
      "weight_kg",
      "source",
    ];
    const rows = items.map((w) =>
      headers.map((h) => JSON.stringify(w[h] ?? "")).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pesaxes_sigeus_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!allowed) {
    return (
      <div className="max-w-2xl" data-testid="pesaxes-restricted">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <div className="font-serif text-xl text-amber-900 mb-2">
            {t("fase3.restricted_role")}
          </div>
          <div className="text-sm text-amber-800">
            {t("fase3.admin_supervisor_only")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px]" data-testid="pesaxes-root">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
            siGEUS · Distromel · MRV
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-salnes-ink leading-tight">
            {lang === "es" ? "Pesajes" : "Pesaxes"}
          </h1>
          <p className="mt-2 text-salnes-stone text-sm max-w-2xl">
            {lang === "es"
              ? "Eventos reales de pesaje/RFID sincronizados desde siGEUS (Distromel S.L.). Solo lectura — no admite entrada manual."
              : "Eventos reais de pesaxe/RFID sincronizados desde siGEUS (Distromel S.L.). Só lectura — non admite entrada manual."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={exportCsv}
            disabled={items.length === 0}
            data-testid="pesaxes-export-csv"
          >
            <Download size={14} className="mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={reset}
            data-testid="pesaxes-reset"
          >
            <RotateCcw size={14} className="mr-2" />
            {lang === "es" ? "Reiniciar filtros" : "Reiniciar filtros"}
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile
          label={lang === "es" ? "Eventos filtrados" : "Eventos filtrados"}
          value={fmtInt(total)}
          testid="pesaxes-kpi-events"
        />
        <StatTile
          label="kg totais"
          value={fmtInt(kgTotal)}
          testid="pesaxes-kpi-kg"
        />
        <StatTile
          label={lang === "es" ? "Matrículas activas" : "Matrículas activas"}
          value={fmtInt(plates.length)}
          testid="pesaxes-kpi-plates"
        />
        <StatTile
          label={lang === "es" ? "Tipos de residuo" : "Tipos de residuo"}
          value={fmtInt(wasteTypes.length)}
          testid="pesaxes-kpi-waste-types"
        />
      </div>

      {/* Filter bar */}
      <div
        className="rounded-2xl border hairline bg-white p-4 mb-4"
        data-testid="pesaxes-filters"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
          <Filter size={12} /> {lang === "es" ? "Filtros" : "Filtros"}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <Field label={lang === "es" ? "Desde" : "Desde"}>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setPage(0);
                setDateFrom(e.target.value);
              }}
              data-testid="pesaxes-date-from"
            />
          </Field>
          <Field label={lang === "es" ? "Hasta" : "Ata"}>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setPage(0);
                setDateTo(e.target.value);
              }}
              data-testid="pesaxes-date-to"
            />
          </Field>
          <Field label={lang === "es" ? "Matrícula" : "Matrícula"}>
            <Select
              value={plate || ANY}
              onValueChange={(v) => {
                setPage(0);
                setPlate(v === ANY ? "" : v);
              }}
            >
              <SelectTrigger data-testid="pesaxes-plate">
                <SelectValue placeholder={lang === "es" ? "Todas" : "Todas"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>
                  {lang === "es" ? "Todas" : "Todas"}
                </SelectItem>
                {plates.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={lang === "es" ? "Tipo de residuo" : "Tipo de residuo"}>
            <Select
              value={wasteType || ANY}
              onValueChange={(v) => {
                setPage(0);
                setWasteType(v === ANY ? "" : v);
              }}
            >
              <SelectTrigger data-testid="pesaxes-waste-type">
                <SelectValue placeholder={lang === "es" ? "Todos" : "Todos"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>
                  {lang === "es" ? "Todos" : "Todos"}
                </SelectItem>
                {wasteTypes.map((w) => (
                  <SelectItem key={w} value={w}>
                    {w}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={lang === "es" ? "Municipio" : "Concello"}>
            <Select
              value={municipality || ANY}
              onValueChange={(v) => {
                setPage(0);
                setMunicipality(v === ANY ? "" : v);
              }}
            >
              <SelectTrigger data-testid="pesaxes-municipality">
                <SelectValue placeholder={lang === "es" ? "Todos" : "Todos"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>
                  {lang === "es" ? "Todos" : "Todos"}
                </SelectItem>
                {munis.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-salnes-sand/40 text-salnes-stone">
            <tr>
              <Th>{lang === "es" ? "Fecha" : "Data"}</Th>
              <Th>{lang === "es" ? "Matrícula" : "Matrícula"}</Th>
              <Th>{lang === "es" ? "Residuo" : "Residuo"}</Th>
              <Th>{lang === "es" ? "Municipio" : "Concello"}</Th>
              <Th>{lang === "es" ? "Punto" : "Punto"}</Th>
              <Th>{lang === "es" ? "Contenedor" : "Contedor"}</Th>
              <Th className="text-right">kg</Th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-10 text-salnes-stone text-sm"
                  data-testid="pesaxes-loading"
                >
                  …
                </td>
              </tr>
            )}
            {!isLoading &&
              items.map((w) => (
                <tr
                  key={w.weighin_id}
                  data-testid={`weighin-row-${w.weighin_id}`}
                >
                  <Td className="whitespace-nowrap">
                    {fmtDateTime(w.weighing_date)}
                  </Td>
                  <Td className="font-mono text-xs">
                    {w.vehicle_plate || "—"}
                  </Td>
                  <Td>{w.waste_type || "—"}</Td>
                  <Td>{w.municipality || "—"}</Td>
                  <Td className="tabular-nums text-xs text-salnes-stone">
                    #{w.col_point_id || "—"}
                  </Td>
                  <Td className="font-mono text-[10px] text-salnes-stone truncate max-w-[160px]">
                    {w.container_code || "—"}
                  </Td>
                  <Td className="text-right tabular-nums font-medium">
                    {fmtDec(w.weight_kg, 0)}
                  </Td>
                </tr>
              ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-14 text-salnes-stone text-sm"
                  data-testid="pesaxes-empty"
                >
                  {lang === "es"
                    ? "Sin resultados para los filtros seleccionados."
                    : "Sen resultados para os filtros seleccionados."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between mt-4 text-xs text-salnes-stone"
          data-testid="pesaxes-pagination"
        >
          <div>
            {lang === "es" ? "Página" : "Páxina"}{" "}
            <span className="font-medium text-salnes-ink">{page + 1}</span> /{" "}
            {totalPages}
            <span className="ml-2 tabular-nums">
              ({fmtInt(total)}{" "}
              {lang === "es" ? "resultados" : "resultados"})
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="pesaxes-prev-page"
            >
              {lang === "es" ? "Anterior" : "Anterior"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              data-testid="pesaxes-next-page"
            >
              {lang === "es" ? "Siguiente" : "Seguinte"}
            </Button>
          </div>
        </div>
      )}

      <p className="mt-6 text-[10px] text-salnes-stone italic flex items-center gap-1.5">
        <Scale size={11} />
        {lang === "es"
          ? "Fuente autoritativa: siGEUS · Distromel S.L. — eventos con incidence_group=201 (Recogida pesaje/RFID). Actualizados por el cron cada 15 min."
          : "Fonte autoritativa: siGEUS · Distromel S.L. — eventos con incidence_group=201 (Recollida pesaxe/RFID). Actualizados polo cron cada 15 min."}
      </p>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
      {label}
    </Label>
    {children}
  </div>
);

const Th = ({ children, className = "" }) => (
  <th
    className={`text-left text-[10px] uppercase tracking-[0.18em] font-medium px-4 py-3 ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

function StatTile({ label, value, testid }) {
  return (
    <div
      className="rounded-2xl border hairline bg-white p-4"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
        {label}
      </div>
      <div className="mt-1 font-serif text-2xl text-salnes-ink tabular-nums">
        {value ?? "—"}
      </div>
    </div>
  );
}
